import { useState } from 'react';
import { categoryPillStyle } from '../../lib/categoryPalette.js';
import { formatWelcomeDate } from '../../lib/welcomeEditions.js';

/**
 * @typedef {object} WelcomeEditionRow
 * @property {string} isoDate
 * @property {string} [subjectLine]
 * @property {string} [introSummary]
 * @property {number} [articleCount]
 * @property {string[]} [categories]
 * @property {{ url: string, alt?: string } | null} [heroImage]
 */

/**
 * Decorative edition tile for the welcome landing scroller.
 * @param {{ row: WelcomeEditionRow, linked?: boolean }} props
 */
export default function WelcomeEditionCard({ row, linked = false }) {
  const [imageOk, setImageOk] = useState(Boolean(row.heroImage?.url));
  const hasImage = Boolean(row.heroImage?.url) && imageOk;

  return (
    <article
      className={[
        'welcome-edition-card w-full bg-white rounded overflow-hidden border-2 border-[#0A0A0A] shadow-sm flex flex-col',
        linked ? 'h-full' : 'shrink-0 max-w-[288px] mx-auto',
      ].join(' ')}
      aria-hidden={linked ? undefined : true}
    >
      <div
        className={[
          'h-[103px] shrink-0 bg-hxd-surface overflow-hidden',
          hasImage ? '' : 'flex items-center justify-center',
        ].join(' ')}
      >
        {hasImage ? (
          <img
            src={row.heroImage.url}
            alt={row.heroImage.alt || ''}
            className="welcome-edition-photo w-full h-full object-cover"
            loading="lazy"
            onError={() => setImageOk(false)}
          />
        ) : (
          <span className="font-display text-[12px] font-bold tracking-display-sm uppercase text-hxd-muted px-2 text-center">
            HXD Brief
          </span>
        )}
      </div>

      <div className="px-3 py-2 border-b border-hxd-border-soft shrink-0">
        <div className="font-mono text-[13px] font-bold tracking-[0.06em] uppercase text-[#525252]">
          {formatWelcomeDate(row.isoDate)}
        </div>
        <div className="text-[13px] text-hxd-muted mt-0.5">
          {row.articleCount ?? 0}{' '}
          {(row.articleCount ?? 0) === 1 ? 'article' : 'articles'}
        </div>
      </div>

      <div className="px-3 py-2.5 flex-1">
        <h3 className="text-[16px] font-bold text-hxd-ink leading-snug m-0 line-clamp-2">
          {row.subjectLine || 'Morning Brief'}
        </h3>
        {row.introSummary && (
          <p className="text-[14px] text-hxd-muted leading-snug mt-1 mb-0 line-clamp-2">
            {row.introSummary}
          </p>
        )}
      </div>

      {row.categories && row.categories.length > 0 && (
        <div className="px-3 pb-2.5 flex flex-wrap gap-1 shrink-0">
          {row.categories.slice(0, 3).map((c) => (
            <span
              key={c}
              className="font-mono text-[12px] font-bold uppercase tracking-[0.14em] rounded-[2px] px-1.5 py-0.5 text-white"
              style={categoryPillStyle(c)}
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
